import { INVOICE_EXPORT_MAX_IDS, parisToday } from "@erp/shared";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Download, FileArchive, Plus, Receipt } from "lucide-react";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { Badge } from "~/components/ui/badge";
import { Button, buttonVariants } from "~/components/ui/button";
import { DataList, type Column } from "~/components/ui/data-list";
import { Page, PageHeader, Panel, SearchInput, Segmented, Toolbar } from "~/components/ui/page";
import { EmptyState, ErrorState, Skeleton } from "~/components/ui/states";
import { InvoiceExportSheet } from "~/features/sales/invoice-export";
import { INVOICE_TONE } from "~/features/sales/status";
import { api, fetchFile, unwrap } from "~/lib/api";
import { cn } from "~/lib/cn";
import { downloadBlob } from "~/lib/csv";
import { errorMessageKey } from "~/lib/errors";
import { listQuery } from "~/lib/forms";
import { useFormat, useT } from "~/lib/i18n";
import { useCan } from "~/lib/session";
import { useInfiniteList } from "~/lib/use-list";
import { HelpTip } from "~/components/ui/help-tip";

const FILTERS = ["all", "unpaid", "overdue", "paid", "drafts", "creditNotes", "shopify", "iabako"] as const;
type Filter = (typeof FILTERS)[number];

const search = z.object({
  q: z.string().optional(),
  filter: z.enum(FILTERS).optional(),
  sort: z.enum(["issueDate", "number", "grossCents"]).optional(),
  dir: z.enum(["asc", "desc"]).optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute("/_app/invoices/")({ validateSearch: search, component: Invoices });

function fetchInvoices(params: Search & { offset: number; limit: number }) {
  const { filter, ...rest } = params;
  const scope =
    filter === "unpaid" || filter === "overdue" || filter === "paid"
      ? { payment: filter }
      : filter === "drafts"
        ? { status: "draft" as const }
        : filter === "creditNotes"
          ? { kind: "credit_note" as const }
          : filter === "shopify" || filter === "iabako"
            ? { issuer: filter }
            : {};
  return unwrap(api.invoices.get({ query: listQuery({ ...rest, ...scope }) }));
}
type Row = Awaited<ReturnType<typeof fetchInvoices>>["items"][number];

function Invoices() {
  const t = useT();
  const format = useFormat();
  const can = useCan();
  const navigate = useNavigate({ from: Route.fullPath });
  const filters = Route.useSearch();
  const [exporting, setExporting] = useState(false);
  // The selection belongs to one search: a new filter or query starts an empty one.
  const filterKey = JSON.stringify(filters);
  const [picked, setPicked] = useState<{ key: string; ids: Set<string> }>(() => ({ key: filterKey, ids: new Set() }));
  const selected = picked.key === filterKey ? picked.ids : new Set<string>();
  const setSelected = (ids: Set<string>) => setPicked({ key: filterKey, ids });
  const list = useInfiniteList("invoices", filters, fetchInvoices);
  const bulk = useMutation({
    mutationFn: () => fetchFile("/invoices/export", { ids: [...selected].join(","), includeExternal: "1" }),
    onSuccess: (blob) => {
      downloadBlob(t("invoices.bulk.filename", { date: parisToday() }), blob);
      toast.success(t("invoices.bulk.downloaded"));
      setSelected(new Set());
    },
    onError: (e) => toast.error(t(errorMessageKey(e))),
  });
  const downloadSelection = () => {
    if (selected.size > INVOICE_EXPORT_MAX_IDS)
      return void toast.error(t("invoices.bulk.tooMany", { max: INVOICE_EXPORT_MAX_IDS }));
    bulk.mutate();
  };
  const summary = list.firstPage?.summary;
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  const columns: Column<Row>[] = [
    {
      id: "number",
      header: t("invoices.fields.number"),
      sortKey: "number",
      cell: (r) => (
        <div className="py-1.5">
          <p className="flex items-center gap-2 font-medium tabular">
            {r.number ?? t("invoices.statuses.draft")}
            {r.issuer === "shopify" ? <Badge tone="info">{t("invoices.external.badge")}</Badge> : null}
            {r.issuer === "iabako" ? <Badge>{t("invoices.issuers.iabako")}</Badge> : null}
          </p>
          <p className="text-xs text-fg-3">{t(`invoices.kinds.${r.kind}`)}</p>
        </div>
      ),
    },
    { id: "customer", header: t("invoices.fields.customer"), cell: (r) => r.customerName, className: "max-w-[260px] truncate" },
    {
      id: "date",
      header: t("invoices.fields.issueDate"),
      sortKey: "issueDate",
      cell: (r) => (r.issueDate ? format.date(r.issueDate, "short") : ""),
      className: "text-fg-2",
    },
    {
      id: "due",
      header: t("invoices.fields.dueDate"),
      cell: (r) =>
        r.dueDate && r.kind !== "credit_note" ? (
          <span className={r.overdue ? "font-medium text-danger" : "text-fg-2"}>{format.date(r.dueDate, "short")}</span>
        ) : (
          ""
        ),
      minWidth: "lg",
    },
    {
      id: "total",
      header: t("invoices.fields.total"),
      sortKey: "grossCents",
      align: "end",
      cell: (r) => format.money(r.grossCents),
    },
    {
      id: "balance",
      header: t("invoices.fields.balance"),
      align: "end",
      cell: (r) =>
        r.status === "issued" && r.kind !== "credit_note" ? (
          <span className={cn(r.balanceCents > 0 ? "font-medium" : "text-fg-3")}>{format.money(r.balanceCents)}</span>
        ) : (
          ""
        ),
    },
    {
      id: "status",
      header: "",
      cell: (r) =>
        r.overdue ? (
          <Badge tone="danger">{t("invoices.overdue")}</Badge>
        ) : r.status === "issued" && r.kind !== "credit_note" && r.balanceCents <= 0 ? (
          <Badge tone="success">{t("invoices.filters.paid")}</Badge>
        ) : (
          <Badge tone={INVOICE_TONE[r.status]}>{t(`invoices.statuses.${r.status}`)}</Badge>
        ),
    },
    {
      id: "pdf",
      header: "",
      align: "end",
      cell: (r) =>
        r.status === "issued" ? (
          <a
            href={`/api/invoices/${r.id}/pdf?download=1`}
            download
            onClick={(e) => e.stopPropagation()}
            className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
            aria-label={t("invoices.actions.download")}
            title={t("invoices.actions.download")}
          >
            <Download />
          </a>
        ) : null,
    },
  ];

  const newButton = can("invoices:issue") ? (
    <Link to="/invoices/new" className={buttonVariants({ variant: "primary" })}>
      <Plus />
      {t("invoices.new")}
    </Link>
  ) : null;

  return (
    <Page>
      <PageHeader
        title={t("invoices.title")}
        actions={
          <>
            <span className="inline-flex items-center gap-0.5">
              <Button onClick={() => setExporting(true)}>
                <FileArchive />
                <span className="max-sm:sr-only">{t("invoices.export.action")}</span>
              </Button>
              <HelpTip id="invoice.export" />
            </span>
            {newButton}
          </>
        }
      />
      <dl className="mb-5 flex flex-wrap gap-x-10 gap-y-3">
        {summary ? (
          <>
            <div>
              <dt className="text-xs text-fg-3">{t("invoices.summary.receivable")}</dt>
              <dd className="font-display text-2xl font-medium tabular">{format.money(summary.receivableCents)}</dd>
            </div>
            <div>
              <dt className="text-xs text-fg-3">{t("invoices.summary.overdue")}</dt>
              <dd className={cn("font-display text-2xl font-medium tabular", summary.overdueCents > 0 && "text-danger")}>
                {format.money(summary.overdueCents)}
              </dd>
            </div>
          </>
        ) : (
          <Skeleton className="h-12 w-72" />
        )}
      </dl>
      <Panel>
        <Toolbar>
          <SearchInput
            value={filters.q ?? ""}
            onChange={(q) => setSearch({ q: q || undefined })}
            placeholder={t("invoices.searchPlaceholder")}
          />
          {selected.size > 0 ? (
            <span className="flex items-center gap-2">
              <Button variant="primary" onClick={downloadSelection} loading={bulk.isPending}>
                <Download />
                {t("invoices.bulk.download", { count: selected.size })}
              </Button>
              <Button variant="ghost" onClick={() => setSelected(new Set())}>
                {t("invoices.bulk.clear")}
              </Button>
            </span>
          ) : null}
          <Segmented<Filter>
            label={t("common.actions.filter")}
            value={filters.filter ?? "all"}
            onChange={(v) => setSearch({ filter: v === "all" ? undefined : v })}
            options={FILTERS.map((f) => ({ value: f, label: t(`invoices.filters.${f}`) }))}
          />
        </Toolbar>
        {list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : (
          <DataList
            rows={list.rows}
            rowKey={(r) => r.id}
            columns={columns}
            card={(r) => (
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{r.customerName}</p>
                  <p className="text-xs text-fg-3 tabular">
                    {r.number ?? t("invoices.statuses.draft")} — {t(`invoices.kinds.${r.kind}`)}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="tabular">{format.money(r.grossCents)}</p>
                  {r.overdue ? <Badge tone="danger">{t("invoices.overdue")}</Badge> : null}
                  {r.issuer === "shopify" ? <Badge tone="info">{t("invoices.external.badge")}</Badge> : null}
                  {r.issuer === "iabako" ? <Badge>{t("invoices.issuers.iabako")}</Badge> : null}
                </div>
              </div>
            )}
            onOpen={(r) => void navigate({ to: "/invoices/$id", params: { id: r.id } })}
            selection={{
              selected,
              onChange: setSelected,
              selectable: (r) => r.status === "issued",
              label: (r) => r.number ?? r.customerName,
            }}
            sort={{ key: filters.sort, dir: filters.dir ?? "desc" }}
            onSortChange={(s) => setSearch({ sort: s.key as Search["sort"], dir: s.dir })}
            loading={list.isLoading}
            hasMore={list.hasMore}
            loadingMore={list.loadingMore}
            onLoadMore={list.loadMore}
            empty={
              filters.q || filters.filter ? (
                <EmptyState title={t("common.states.noResults")} body={t("common.states.noResultsHint")} />
              ) : (
                <EmptyState
                  icon={<Receipt />}
                  title={t("invoices.emptyTitle")}
                  body={t("invoices.emptyBody")}
                  action={newButton}
                />
              )
            }
          />
        )}
      </Panel>
      <InvoiceExportSheet open={exporting} onOpenChange={setExporting} />
    </Page>
  );
}
